import type { Root } from 'react-dom/client';
import { authLoadingManager } from '../shared-lib/loader/auth-loading-manager';
import { loadFullAppStyles } from '../shared-lib/utils/deferred-app-styles';
import { isHomeLandingPath } from '../shared-lib/utils/public-marketing-path';
import {
  applyBodyClassForPath,
  markUpgradedFromShell,
} from '../shared-lib/utils/shell-upgrade';
import { releaseHomeShellEntry, releaseHomeShellForAppRoute } from '../shared-lib/utils/home-fonts-ready';

let upgradeStarted = false;
let fullAppStylesPrepared = false;
let fullAppChunkPromise: Promise<void> | null = null;

/** Warm full-app styles + chunks while the home shell is idle. */
export function prepareFullAppUpgrade(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (!fullAppStylesPrepared) {
    fullAppStylesPrepared = true;
    require('../index.scss');
    loadFullAppStyles();
  }

  return preloadFullAppUpgrade();
}

/** Warm full-app JS chunks without mounting. */
export function preloadFullAppUpgrade(): Promise<void> {
  if (upgradeStarted || typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (!fullAppChunkPromise) {
    fullAppChunkPromise = Promise.all([
      import('./full-app-entry'),
      import('../virtco/app/App'),
      import('../shared-lib/layout/topheader-component/top-headernew'),
      import('../virtco/components/search-module/search-consultation'),
    ]).then(() => undefined);
  }

  return fullAppChunkPromise;
}

export function upgradeToFullApp(root: Root): void {
  if (upgradeStarted || typeof window === 'undefined') {
    return;
  }
  upgradeStarted = true;

  markUpgradedFromShell();

  if (isHomeLandingPath(window.location.pathname)) {
    releaseHomeShellEntry();
  } else {
    releaseHomeShellForAppRoute();
  }

  document.documentElement.classList.add('virtup-shell-upgrading');

  if (!fullAppStylesPrepared) {
    fullAppStylesPrepared = true;
    require('../index.scss');
    loadFullAppStyles();
  }

  applyBodyClassForPath(window.location.pathname);
  authLoadingManager.markAppBootstrapComplete();

  void preloadFullAppUpgrade().then(() => {
    import('./full-app-entry').then(({ mountFullApp }) => {
      mountFullApp(root, { seamless: true });
      window.requestAnimationFrame(() => {
        document.documentElement.classList.remove('virtup-shell-upgrading');
      });
    });
  });
}

export function isFullAppUpgradeStarted(): boolean {
  return upgradeStarted;
}

const PROACTIVE_UPGRADE_FALLBACK_MS = 12000;

/** Preload full app in background; mount only after user intent to avoid post-reveal blink. */
export function scheduleProactiveFullAppUpgrade(root: Root): () => void {
  if (typeof window === 'undefined' || upgradeStarted) {
    return () => undefined;
  }

  let cancelled = false;
  let fallbackTimer: number | undefined;

  const cleanup = () => {
    cancelled = true;
    if (fallbackTimer !== undefined) {
      window.clearTimeout(fallbackTimer);
    }
    window.removeEventListener('pointerdown', onUserIntent);
    window.removeEventListener('keydown', onUserIntent);
    window.removeEventListener('touchstart', onUserIntent);
    window.removeEventListener('scroll', onUserIntent);
  };

  const runUpgrade = () => {
    if (cancelled || upgradeStarted) {
      return;
    }

    cleanup();
    void prepareFullAppUpgrade().then(() => {
      if (cancelled || upgradeStarted) {
        return;
      }
      if (!isHomeLandingPath(window.location.pathname)) {
        return;
      }
      upgradeToFullApp(root);
    });
  };

  const onUserIntent = () => {
    runUpgrade();
  };

  void prepareFullAppUpgrade();

  window.addEventListener('pointerdown', onUserIntent, { once: true, passive: true });
  window.addEventListener('keydown', onUserIntent, { once: true });
  window.addEventListener('touchstart', onUserIntent, { once: true, passive: true });
  window.addEventListener('scroll', onUserIntent, { once: true, passive: true });
  fallbackTimer = window.setTimeout(runUpgrade, PROACTIVE_UPGRADE_FALLBACK_MS);

  return cleanup;
}
